import {
  BusinessTranslation,
  BusinessTranslationDictionary,
  businessTranslations,
} from "../legacy/data/legacyBusinessTranslations.data";
import { LocalizedText } from "../domain/shared/localized-text.types";
import { ServiceCategory } from "../domain/services/service.types";
import type { BaseBusiness } from "../legacy/types/legacyBusiness.types";
import {
  LegacyBusinessAdapterOptions,
  LegacyProductContent,
  LegacyServiceContent,
  LegacyTourContent,
  LegacyVideoContent,
} from "./legacyBusinessAdapter";

export interface TranslationIssue {
  language: "es" | "en";
  path: string;
  reason: "missing" | "empty";
}

const nonEmpty = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const requiredSpanish = (value: string | undefined, path: string): string => {
  const text = nonEmpty(value);
  if (!text) throw new Error(`Missing indispensable Spanish translation at "${path}"`);
  return text;
};

const toLocalizedText = (
  spanish: string | undefined,
  english: string | undefined,
  path: string
): LocalizedText => {
  const es = requiredSpanish(spanish, `${path}.es`);
  const en = nonEmpty(english);
  return en ? { es, en } : { es };
};

const getBusinessTranslation = (
  dictionary: BusinessTranslationDictionary,
  businessId: string,
  language: "es" | "en"
): BusinessTranslation | undefined => {
  const translation = dictionary[businessId];
  if (language === "es" && !translation) {
    throw new Error(`Missing indispensable Spanish translation for business "${businessId}"`);
  }
  return translation;
};

const toProductContent = (
  source: BaseBusiness,
  spanish: BusinessTranslation,
  english?: BusinessTranslation
): Record<string, LegacyProductContent> =>
  Object.fromEntries(
    source.products.map((product) => {
      const path = `businesses.${source.id}.products.${product.id}`;
      return [
        product.id,
        {
          name: toLocalizedText(
            spanish.products?.[product.id]?.name,
            english?.products?.[product.id]?.name,
            `${path}.name`
          ),
          description: toLocalizedText(
            spanish.products?.[product.id]?.description,
            english?.products?.[product.id]?.description,
            `${path}.description`
          ),
        },
      ];
    })
  );

const toTourContent = (
  source: BaseBusiness,
  spanish: BusinessTranslation,
  english?: BusinessTranslation
): Record<string, LegacyTourContent> | undefined => {
  if (!source.tours?.length) return undefined;

  return Object.fromEntries(
    source.tours.map((tour) => {
      const path = `businesses.${source.id}.tours.${tour.id}`;
      const spanishTour = spanish.tours?.[tour.id];
      const englishTour = english?.tours?.[tour.id];
      const status = nonEmpty(spanishTour?.status)
        ? toLocalizedText(spanishTour?.status, englishTour?.status, `${path}.status`)
        : undefined;

      return [
        tour.id,
        {
          name: toLocalizedText(spanishTour?.name, englishTour?.name, `${path}.name`),
          description: toLocalizedText(
            spanishTour?.description,
            englishTour?.description,
            `${path}.description`
          ),
          status,
        },
      ];
    })
  );
};

const toVideoContent = (
  source: BaseBusiness,
  spanish: BusinessTranslation,
  english?: BusinessTranslation
): Record<string, LegacyVideoContent> | undefined => {
  if (!source.videos?.length) return undefined;

  return Object.fromEntries(
    source.videos.map((video) => {
      const path = `businesses.${source.id}.videos.${video.id}`;
      return [
        video.id,
        {
          name: toLocalizedText(
            spanish.videos?.[video.id]?.name,
            english?.videos?.[video.id]?.name,
            `${path}.name`
          ),
          description: toLocalizedText(
            spanish.videos?.[video.id]?.description,
            english?.videos?.[video.id]?.description,
            `${path}.description`
          ),
        },
      ];
    })
  );
};

const toServiceContent = (
  source: BaseBusiness,
  spanish: BusinessTranslation,
  english?: BusinessTranslation
): LegacyServiceContent[] | undefined => {
  if (source.id === "api-g-apiarios-gil") {
    const id = "tour-apiario";
    const tour = source.tours?.find((item) => item.id === id);
    if (!tour) return undefined;

    return [{
      id,
      slug: id,
      name: toLocalizedText(
        spanish.tours?.[id]?.name,
        english?.tours?.[id]?.name,
        `businesses.${source.id}.services.${id}.name`
      ),
      description: toLocalizedText(
        spanish.tours?.[id]?.description,
        english?.tours?.[id]?.description,
        `businesses.${source.id}.services.${id}.description`
      ),
      image: tour.image,
      category: ServiceCategory.EXPERIENCE,
      status: "on-request",
      featured: true,
      order: 1,
      actionIntent: "reserve",
      actionLabel: { es: "Reservar", en: "Book" },
    }];
  }

  if (source.id === "lacteos-don-juan") {
    return [
      {
        id: "soda-don-juan",
        slug: "soda-don-juan",
        name: { es: "Soda Don Juan", en: "Don Juan Eatery" },
        description: { es: "Comida preparada en la finca. Consulta opciones, horarios y disponibilidad.", en: "Food prepared at the farm. Ask about options, opening hours and availability." },
        image: require("../assets/images/finca-ecologica-don-juan/services/menu-soda-don-juan.png"),
        category: ServiceCategory.FOOD,
        status: "on-request",
        order: 1,
        actionIntent: "inquire",
        actionLabel: { es: "Consultar", en: "Inquire" },
      },
      {
        id: "tour-mariposa-don-juan", slug: "tour-mariposa-don-juan",
        name: { es: "Tour de la Mariposa", en: "Butterfly Tour" },
        description: { es: "Metamorfosis y tipos de mariposas\nDegustación de frutas\nVisita a una cabaña antigua\nTaller de artesanías con materia muerta de mariposas", en: "Butterfly metamorphosis and species\nFruit tasting\nVisit to an old cabin\nCraft workshop using naturally deceased butterflies" },
        image: require("../assets/images/finca-ecologica-don-juan/services/tour-mariposa-don-juan.png"),
        category: ServiceCategory.EXPERIENCE, price: { es: "$9.99 / 2 personas", en: "$9.99 / 2 people" },
        status: "on-request", order: 2, actionIntent: "inquire",
        actionLabel: { es: "Consultar tour", en: "Ask about this tour" },
      },
      {
        id: "tour-miel-don-juan", slug: "tour-miel-don-juan", name: { es: "Tour de la Miel", en: "Honey Tour" },
        description: { es: "Charla informativa sobre las abejas\nDegustación de miel\nTaller de miel\nUso de overol especial apícola", en: "Informative talk about bees\nHoney tasting\nHoney workshop\nUse of a special beekeeping suit" },
        image: require("../assets/images/finca-ecologica-don-juan/services/tour-miel-don-juan.png"), category: ServiceCategory.EXPERIENCE,
        price: { es: "$12 / 2 personas", en: "$12 / 2 people" }, status: "on-request", order: 3, actionIntent: "inquire", actionLabel: { es: "Consultar tour", en: "Ask about this tour" },
      },
      {
        id: "tour-agua-don-juan", slug: "tour-agua-don-juan", name: { es: "Tour del Agua", en: "Water Tour" },
        description: { es: "Nacientes de agua\nCharla sobre el cuido de las nacientes\nSenderismo por bosque primario", en: "Natural springs\nTalk about spring conservation\nHiking through primary forest" },
        image: require("../assets/images/finca-ecologica-don-juan/services/tour-agua-don-juan.png"), category: ServiceCategory.EXPERIENCE,
        price: { es: "$6.55 / 2 personas", en: "$6.55 / 2 people" }, status: "on-request", order: 4, actionIntent: "inquire", actionLabel: { es: "Consultar tour", en: "Ask about this tour" },
      },
      {
        id: "tour-voisin-don-juan", slug: "tour-voisin-don-juan", name: { es: "Tour del Voisin", en: "Voisin Tour" },
        description: { es: "Charla informativa y recorrido por la finca\nOrdeño, arreo y alimentación del ganado\nVisita a la catarata", en: "Informative talk and farm tour\nMilking, herding and feeding cattle\nVisit to the waterfall" },
        image: require("../assets/images/finca-ecologica-don-juan/services/tour-voisin-don-juan.png"), category: ServiceCategory.EXPERIENCE,
        price: { es: "$12 / 2 personas", en: "$12 / 2 people" }, status: "on-request", order: 5, actionIntent: "inquire", actionLabel: { es: "Consultar tour", en: "Ask about this tour" },
      },
      {
        id: "tour-senderismo-don-juan", slug: "tour-senderismo-don-juan", name: { es: "Tour de Senderismo", en: "Hiking Tour" },
        description: { es: "Observación de flora y fauna\nVisita a área verde\nCharla sobre bosques primarios", en: "Flora and fauna observation\nVisit to a green area\nTalk about primary forests" },
        image: require("../assets/images/finca-ecologica-don-juan/services/tour-senderismo-don-juan.png"), category: ServiceCategory.EXPERIENCE,
        price: { es: "$6.99 / 2 personas", en: "$6.99 / 2 people" }, status: "on-request", order: 6, actionIntent: "inquire", actionLabel: { es: "Consultar tour", en: "Ask about this tour" },
      },
    ];
  }

  if (source.id === "pasteleria-reposteria-jessy") {
    const id = "bocadillos-para-eventos";
    const product = source.products.find((item) => item.id === id);
    if (!product) return undefined;

    return [{
      id,
      slug: id,
      name: toLocalizedText(
        spanish.products?.[id]?.name,
        english?.products?.[id]?.name,
        `businesses.${source.id}.services.${id}.name`
      ),
      description: toLocalizedText(
        spanish.products?.[id]?.description,
        english?.products?.[id]?.description,
        `businesses.${source.id}.services.${id}.description`
      ),
      image: product.image,
      category: ServiceCategory.EVENT,
      price: product.price ? { es: product.price } : undefined,
      status: "on-request",
      featured: true,
      order: 1,
      actionIntent: "request-quote",
      actionLabel: { es: "Solicitar cotización", en: "Request a quote" },
    }];
  }

  return undefined;
};

export const getLegacyBusinessAdapterOptions = (
  source: BaseBusiness,
  dictionaries: typeof businessTranslations = businessTranslations
): LegacyBusinessAdapterOptions => {
  const spanish = getBusinessTranslation(dictionaries.es, source.id, "es");
  if (!spanish) {
    throw new Error(`Missing indispensable Spanish translation for business "${source.id}"`);
  }
  const english = getBusinessTranslation(dictionaries.en, source.id, "en");

  return {
    name: toLocalizedText(spanish.name, english?.name, `businesses.${source.id}.name`),
    summary: toLocalizedText(
      spanish.shortDescription,
      english?.shortDescription,
      `businesses.${source.id}.shortDescription`
    ),
    description: toLocalizedText(
      spanish.longDescription,
      english?.longDescription,
      `businesses.${source.id}.longDescription`
    ),
    products: toProductContent(source, spanish, english),
    tours: toTourContent(source, spanish, english),
    videos: toVideoContent(source, spanish, english),
    services: toServiceContent(source, spanish, english),
  };
};

const inspectValue = (
  issues: TranslationIssue[],
  language: "es" | "en",
  path: string,
  value: string | undefined
) => {
  if (value === undefined) issues.push({ language, path, reason: "missing" });
  else if (!nonEmpty(value)) issues.push({ language, path, reason: "empty" });
};

export const getBusinessTranslationIssues = (
  source: BaseBusiness,
  dictionaries: typeof businessTranslations = businessTranslations
): TranslationIssue[] => {
  const issues: TranslationIssue[] = [];

  (["es", "en"] as const).forEach((language) => {
    const translation = dictionaries[language][source.id];
    inspectValue(issues, language, `businesses.${source.id}.name`, translation?.name);
    inspectValue(
      issues,
      language,
      `businesses.${source.id}.shortDescription`,
      translation?.shortDescription
    );
    inspectValue(
      issues,
      language,
      `businesses.${source.id}.longDescription`,
      translation?.longDescription
    );
    source.products.forEach((product) => {
      inspectValue(
        issues,
        language,
        `businesses.${source.id}.products.${product.id}.name`,
        translation?.products?.[product.id]?.name
      );
      inspectValue(
        issues,
        language,
        `businesses.${source.id}.products.${product.id}.description`,
        translation?.products?.[product.id]?.description
      );
    });
    source.tours?.forEach((tour) => {
      inspectValue(
        issues,
        language,
        `businesses.${source.id}.tours.${tour.id}.name`,
        translation?.tours?.[tour.id]?.name
      );
      inspectValue(
        issues,
        language,
        `businesses.${source.id}.tours.${tour.id}.description`,
        translation?.tours?.[tour.id]?.description
      );
    });
    source.videos?.forEach((video) => {
      inspectValue(
        issues,
        language,
        `businesses.${source.id}.videos.${video.id}.name`,
        translation?.videos?.[video.id]?.name
      );
      inspectValue(
        issues,
        language,
        `businesses.${source.id}.videos.${video.id}.description`,
        translation?.videos?.[video.id]?.description
      );
    });
  });

  return issues;
};
